import React, { useState, useEffect } from 'react';
import { inventoryAPI } from '../services/api';

const Inventory = () => {
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    catalog_id:'',
    custom_product_name:'',
    sku:'', serial_number:'',
    grade:'A', order_type:'sale',
    sale_price:'', rental_price_monthly:'',
    security_deposit:'',
    quantity_total:1,
    condition_notes:'',
  });

  // Detail / Edit / Delete state
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    custom_product_name:'', serial_number:'',
    sku:'', grade:'A', order_type:'sale',
    sale_price:'', rental_price_monthly:'',
    security_deposit:'', quantity_total:1,
    condition_notes:'',
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Bulk upload state
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [uploadingBulk, setUploadingBulk] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkError, setBulkError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [itemsRes, summaryRes, catalogRes] =
        await Promise.all([
          inventoryAPI.getAll(),
          inventoryAPI.getSummary(),
          inventoryAPI.getCatalog(),
        ]);
      setItems(itemsRes.data.data);
      setSummary(summaryRes.data.data);
      setCatalog(catalogRes.data.data.flat_list);
    } catch (err) {
      console.error('Failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadFiltered = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filterType) params.order_type = filterType;
      const res = await inventoryAPI.getAll(params);
      setItems(res.data.data);
    } catch (err) {
      console.error('Failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!form.catalog_id &&
        !form.custom_product_name) {
      setError(
        'Select a product or enter custom name');
      return;
    }
    if (!form.order_type) {
      setError('Order type is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await inventoryAPI.add({
        ...form,
        catalog_id: form.catalog_id || null,
        sale_price: form.sale_price || null,
        rental_price_monthly:
          form.rental_price_monthly || null,
        security_deposit:
          form.security_deposit || null,
      });
      setForm({
        catalog_id:'',
        custom_product_name:'',
        sku:'', serial_number:'',
        grade:'A', order_type:'sale',
        sale_price:'',
        rental_price_monthly:'',
        security_deposit:'',
        quantity_total:1,
        condition_notes:'',
      });
      setShowAdd(false);
      await loadData();
    } catch (err) {
      setError(
        err.response?.data?.message
        || 'Failed to add item');
    } finally {
      setSaving(false);
    }
  };

  const loadDetail = async (id) => {
    try {
      const res = await inventoryAPI.getOne(id);
      setDetail(res.data.data);
    } catch (err) {
      console.error('Failed:', err);
    }
  };

  const openEdit = () => {
    const item = detail.item;
    setEditForm({
      custom_product_name: item.custom_product_name || '',
      serial_number: item.serial_number || '',
      sku: item.sku || '',
      grade: item.grade || 'A',
      order_type: item.order_type || 'sale',
      sale_price: item.sale_price || '',
      rental_price_monthly: item.rental_price_monthly || '',
      security_deposit: item.security_deposit || '',
      quantity_total: item.quantity_total || 1,
      condition_notes: item.condition_notes || '',
    });
    setEditError('');
    setEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.order_type) {
      setEditError('Order type is required');
      return;
    }
    setSavingEdit(true);
    setEditError('');
    try {
      await inventoryAPI.update(selected.id, editForm);
      await loadDetail(selected.id);
      setEditing(false);
      await loadData();
    } catch (err) {
      setEditError(
        err.response?.data?.message
        || 'Failed to update item');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm(
      'Delete this inventory item? This cannot be ' +
      'undone from the dashboard.'
    )) return;
    setDeleting(true);
    try {
      await inventoryAPI.delete(id);
      await loadData();
      setSelected(null);
      setDetail(null);
    } catch (err) {
      alert(
        err.response?.data?.message
        || 'Failed to delete item');
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await inventoryAPI.downloadTemplate();
      const url = window.URL.createObjectURL(
        new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download',
        'ironwall_inventory_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download template');
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) {
      setBulkError('Please choose a file first');
      return;
    }
    setUploadingBulk(true);
    setBulkError('');
    setBulkResult(null);
    try {
      const formData = new FormData();
      formData.append('file', bulkFile);
      const res = await inventoryAPI.bulkUpload(formData);
      setBulkResult(res.data.data);
      setBulkFile(null);
      await loadData();
    } catch (err) {
      setBulkError(
        err.response?.data?.message
        || 'Upload failed. Please check your file and try again.');
    } finally {
      setUploadingBulk(false);
    }
  };

  const inputStyle = {
    width:'100%',
    background:'var(--surface2)',
    border:'1px solid var(--border2)',
    color:'var(--text)',
    padding:'10px 14px',
    borderRadius:'8px',
    fontSize:'13.5px',
    outline:'none',
  };

  const labelStyle = {
    display:'block',
    fontSize:'11px',
    fontFamily:'DM Mono,monospace',
    color:'var(--text3)',
    letterSpacing:'1px',
    textTransform:'uppercase',
    marginBottom:'6px',
  };

  const smallInputStyle = {
    width:'100%',
    background:'var(--surface)',
    border:'1px solid var(--border2)',
    color:'var(--text)',
    padding:'8px 10px',
    borderRadius:'6px',
    fontSize:'12.5px',
    outline:'none',
    marginBottom:'10px',
  };

  const smallLabelStyle = {
    display:'block',
    fontSize:'10px',
    fontFamily:'DM Mono,monospace',
    color:'var(--text3)',
    letterSpacing:'0.5px',
    textTransform:'uppercase',
    marginBottom:'4px',
  };

  const gradeColor = (grade) => {
    if (grade === 'A' || grade === 'A+')
      return 'var(--green)';
    if (grade === 'B+' || grade === 'B')
      return 'var(--yellow)';
    return 'var(--text3)';
  };

  return (
    <div style={{ padding:'24px' }}>
      {/* Header */}
      <div style={{
        display:'flex',
        justifyContent:'space-between',
        alignItems:'center',
        marginBottom:'24px',
      }}>
        <div>
          <h1 style={{
            fontFamily:'Syne,sans-serif',
            fontSize:'22px', fontWeight:'800',
          }}>
            Inventory
          </h1>
          <p style={{
            color:'var(--text3)',
            fontSize:'13px', marginTop:'4px',
          }}>
            Manage your product inventory
          </p>
        </div>
        <div style={{ display:'flex', gap:'10px' }}>
          <button
            onClick={() => setShowBulkUpload(!showBulkUpload)}
            style={{
              background:'transparent',
              color:'var(--accent)',
              border:'1px solid var(--accent)',
              padding:'10px 20px',
              borderRadius:'8px',
              fontSize:'13px', fontWeight:'600',
              cursor:'pointer',
            }}
          >
            ⇪ Bulk Upload
          </button>
          <button
            onClick={() => setShowAdd(!showAdd)}
            style={{
              background:'var(--accent)',
              color:'var(--bg)', border:'none',
              padding:'10px 20px',
              borderRadius:'8px',
              fontSize:'13px', fontWeight:'600',
              cursor:'pointer',
            }}
          >
            + Add Item
          </button>
        </div>
      </div>

      {/* Bulk Upload Panel */}
      {showBulkUpload && (
        <div style={{
          background:'var(--surface)',
          border:'1px solid var(--border)',
          borderRadius:'12px',
          padding:'24px',
          marginBottom:'20px',
        }}>
          <div style={{
            fontFamily:'Syne,sans-serif',
            fontSize:'15px', fontWeight:'700',
            marginBottom:'8px',
          }}>
            Bulk Upload Inventory
          </div>
          <div style={{
            fontSize:'12.5px',
            color:'var(--text3)',
            marginBottom:'18px',
            lineHeight:'1.5',
          }}>
            Download the template, fill in your items,
            then upload it here. Each row becomes a
            custom inventory item (not linked to your
            product catalog). Accepts .xlsx or .csv.
          </div>

          <button
            onClick={handleDownloadTemplate}
            style={{
              background:'var(--surface2)',
              border:'1px solid var(--border2)',
              color:'var(--text)',
              padding:'9px 18px',
              borderRadius:'8px',
              fontSize:'13px', fontWeight:'600',
              cursor:'pointer',
              marginBottom:'18px',
            }}
          >
            ⬇ Download Template (.xlsx)
          </button>

          <div style={{
            display:'flex',
            gap:'10px',
            alignItems:'center',
            flexWrap:'wrap',
          }}>
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={e => {
                setBulkFile(e.target.files[0] || null);
                setBulkError('');
                setBulkResult(null);
              }}
              style={{
                color:'var(--text2)',
                fontSize:'13px',
              }}
            />
            <button
              onClick={handleBulkUpload}
              disabled={uploadingBulk || !bulkFile}
              style={{
                background:'var(--accent)',
                color:'var(--bg)',
                border:'none',
                padding:'9px 20px',
                borderRadius:'8px',
                fontSize:'13px', fontWeight:'600',
                cursor: (uploadingBulk || !bulkFile)
                  ? 'not-allowed' : 'pointer',
                opacity: (uploadingBulk || !bulkFile)
                  ? 0.5 : 1,
              }}
            >
              {uploadingBulk ? 'Uploading...' : 'Upload'}
            </button>
          </div>

          {bulkError && (
            <div style={{
              background:'rgba(255,77,109,0.1)',
              border:'1px solid rgba(255,77,109,0.3)',
              color:'var(--red)',
              padding:'10px 14px',
              borderRadius:'8px',
              fontSize:'13px',
              marginTop:'16px',
            }}>
              {bulkError}
            </div>
          )}

          {bulkResult && (
            <div style={{ marginTop:'18px' }}>
              <div style={{
                display:'flex', gap:'16px',
                marginBottom:'12px',
              }}>
                <div style={{
                  color:'var(--green)',
                  fontSize:'13px',
                  fontFamily:'DM Mono,monospace',
                }}>
                  ✓ {bulkResult.inserted} added
                </div>
                {bulkResult.failed > 0 && (
                  <div style={{
                    color:'var(--red)',
                    fontSize:'13px',
                    fontFamily:'DM Mono,monospace',
                  }}>
                    ✕ {bulkResult.failed} failed
                  </div>
                )}
              </div>
              {bulkResult.errors?.length > 0 && (
                <div style={{
                  background:'var(--surface2)',
                  border:'1px solid var(--border)',
                  borderRadius:'8px',
                  padding:'12px 14px',
                  maxHeight:'200px',
                  overflowY:'auto',
                }}>
                  {bulkResult.errors.map((e, idx) => (
                    <div key={idx} style={{
                      fontSize:'12px',
                      color:'var(--text2)',
                      padding:'4px 0',
                      borderBottom: idx <
                        bulkResult.errors.length - 1
                        ? '1px solid var(--border)'
                        : 'none',
                    }}>
                      <span style={{
                        color:'var(--yellow)',
                        fontFamily:'DM Mono,monospace',
                      }}>
                        Row {e.row}:
                      </span>{' '}
                      {e.reason}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div style={{
          display:'grid',
          gridTemplateColumns:'repeat(4,1fr)',
          gap:'14px', marginBottom:'20px',
        }}>
          {[
            { label:'Total Items',
              value:summary.total_items,
              color:'var(--accent)' },
            { label:'Available',
              value:summary.available_quantity,
              color:'var(--green)' },
            { label:'Assigned',
              value:summary.assigned_quantity,
              color:'var(--yellow)' },
            { label:'Out of Stock',
              value:summary.out_of_stock,
              color:'var(--red)' },
          ].map(s => (
            <div key={s.label} style={{
              background:'var(--surface)',
              border:'1px solid var(--border)',
              borderRadius:'10px',
              padding:'16px',
              borderTop:`2px solid ${s.color}`,
            }}>
              <div style={{
                fontSize:'11px',
                fontFamily:'DM Mono,monospace',
                color:'var(--text3)',
                letterSpacing:'1px',
                textTransform:'uppercase',
                marginBottom:'8px',
              }}>
                {s.label}
              </div>
              <div style={{
                fontFamily:'Syne,sans-serif',
                fontSize:'28px',
                fontWeight:'800',
                color: s.color,
              }}>
                {s.value || 0}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Item Form */}
      {showAdd && (
        <div style={{
          background:'var(--surface)',
          border:'1px solid var(--border)',
          borderRadius:'12px',
          padding:'24px',
          marginBottom:'20px',
        }}>
          <div style={{
            fontFamily:'Syne,sans-serif',
            fontSize:'15px', fontWeight:'700',
            marginBottom:'20px',
          }}>
            Add Inventory Item
          </div>
          <div style={{
            display:'grid',
            gridTemplateColumns:'1fr 1fr',
            gap:'16px',
          }}>
            <div style={{
              gridColumn:'1 / -1',
            }}>
              <label style={labelStyle}>
                Select From Catalog
              </label>
              <select style={inputStyle}
                value={form.catalog_id}
                onChange={e => setForm({
                  ...form,
                  catalog_id:e.target.value
                })}
              >
                <option value="">
                  — Select product —
                </option>
                {catalog.map(p => (
                  <option key={p.id}
                    value={p.id}>
                    {p.category} —{' '}
                    {p.brand} {p.model}
                  </option>
                ))}
              </select>
            </div>
            <div style={{
              gridColumn:'1 / -1',
            }}>
              <label style={labelStyle}>
                Or Enter Custom Product Name
              </label>
              <input style={inputStyle}
                placeholder="Custom product name (if not in catalog)"
                value={form.custom_product_name}
                onChange={e => setForm({
                  ...form,
                  custom_product_name:
                    e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>
                SKU
              </label>
              <input style={inputStyle}
                placeholder="e.g. LAP-001"
                value={form.sku}
                onChange={e => setForm({
                  ...form, sku:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>
                Serial Number
              </label>
              <input style={inputStyle}
                placeholder="Serial number"
                value={form.serial_number}
                onChange={e => setForm({
                  ...form,
                  serial_number:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>
                Grade
              </label>
              <select style={inputStyle}
                value={form.grade}
                onChange={e => setForm({
                  ...form, grade:e.target.value
                })}
              >
                <option value="New">New</option>
                <option value="A+">
                  Grade A+
                </option>
                <option value="A">Grade A</option>
                <option value="B+">
                  Grade B+
                </option>
                <option value="B">Grade B</option>
                <option value="C">Grade C</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>
                Order Type
              </label>
              <select style={inputStyle}
                value={form.order_type}
                onChange={e => setForm({
                  ...form,
                  order_type:e.target.value
                })}
              >
                <option value="sale">
                  Sale
                </option>
                <option value="rental">
                  Rental
                </option>
                <option value="both">
                  Both
                </option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>
                Sale Price (₹)
              </label>
              <input style={inputStyle}
                type="number"
                placeholder="0.00"
                value={form.sale_price}
                onChange={e => setForm({
                  ...form,
                  sale_price:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>
                Rental / Month (₹)
              </label>
              <input style={inputStyle}
                type="number"
                placeholder="0.00"
                value={form.rental_price_monthly}
                onChange={e => setForm({
                  ...form,
                  rental_price_monthly:
                    e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>
                Security Deposit (₹)
              </label>
              <input style={inputStyle}
                type="number"
                placeholder="0.00"
                value={form.security_deposit}
                onChange={e => setForm({
                  ...form,
                  security_deposit:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>
                Quantity
              </label>
              <input style={inputStyle}
                type="number" min="1"
                value={form.quantity_total}
                onChange={e => setForm({
                  ...form,
                  quantity_total:e.target.value
                })}
              />
            </div>
            <div style={{
              gridColumn:'1 / -1',
            }}>
              <label style={labelStyle}>
                Condition Notes
              </label>
              <input style={inputStyle}
                placeholder="Any condition notes"
                value={form.condition_notes}
                onChange={e => setForm({
                  ...form,
                  condition_notes:e.target.value
                })}
              />
            </div>
          </div>

          {error && (
            <div style={{
              background:
                'rgba(255,77,109,0.1)',
              border:
                '1px solid rgba(255,77,109,0.3)',
              color:'var(--red)',
              padding:'10px 14px',
              borderRadius:'8px',
              fontSize:'13px',
              marginTop:'16px',
            }}>
              {error}
            </div>
          )}

          <div style={{
            display:'flex', gap:'10px',
            marginTop:'20px',
          }}>
            <button onClick={handleAdd}
              disabled={saving}
              style={{
                background:'var(--accent)',
                color:'var(--bg)', border:'none',
                padding:'10px 24px',
                borderRadius:'8px',
                fontSize:'13px', fontWeight:'600',
                cursor: saving
                  ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Saving...' : 'Save Item'}
            </button>
            <button
              onClick={() => {
                setShowAdd(false);
                setError('');
              }}
              style={{
                background:'transparent',
                color:'var(--text2)',
                border:'1px solid var(--border2)',
                padding:'10px 24px',
                borderRadius:'8px',
                fontSize:'13px',
                cursor:'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{
        display:'flex', gap:'10px',
        marginBottom:'16px',
        flexWrap:'wrap',
      }}>
        <div style={{
          display:'flex', gap:'6px',
        }}>
          {[
            { key:'', label:'All' },
            { key:'sale', label:'Sale' },
            { key:'rental', label:'Rental' },
            { key:'both', label:'Both' },
          ].map(f => (
            <button key={f.key}
              onClick={() => {
                setFilterType(f.key);
                setTimeout(loadFiltered, 100);
              }}
              style={{
                padding:'7px 16px',
                borderRadius:'20px',
                border: filterType === f.key
                  ? 'none'
                  : '1px solid var(--border2)',
                background:
                  filterType === f.key
                    ? 'var(--accent)'
                    : 'transparent',
                color: filterType === f.key
                  ? 'var(--bg)'
                  : 'var(--text2)',
                fontSize:'12px',
                fontWeight:'600',
                cursor:'pointer',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div style={{
          display:'flex',
          alignItems:'center',
          background:'var(--surface2)',
          border:'1px solid var(--border2)',
          borderRadius:'8px',
          padding:'0 12px', gap:'8px',
          flex:1, maxWidth:'300px',
        }}>
          <span style={{
            color:'var(--text3)',
          }}>⊞</span>
          <input
            placeholder="Search inventory..."
            value={search}
            onChange={e =>
              setSearch(e.target.value)}
            onKeyPress={e =>
              e.key === 'Enter' && loadFiltered()}
            style={{
              background:'none', border:'none',
              outline:'none', color:'var(--text)',
              fontSize:'13px',
              padding:'9px 0', flex:1,
            }}
          />
        </div>
        <button onClick={loadFiltered} style={{
          background:'var(--surface2)',
          border:'1px solid var(--border2)',
          color:'var(--text2)',
          padding:'8px 16px',
          borderRadius:'8px',
          fontSize:'13px', cursor:'pointer',
        }}>
          Search
        </button>
      </div>

      {/* Main Layout */}
      <div style={{
        display:'grid',
        gridTemplateColumns:
          selected ? '1fr 360px' : '1fr',
        gap:'16px',
      }}>
      {/* Items Table */}
      <div style={{
        background:'var(--surface)',
        border:'1px solid var(--border)',
        borderRadius:'12px',
        overflow:'hidden',
      }}>
        {loading ? (
          <div style={{
            padding:'40px', textAlign:'center',
            color:'var(--text3)',
            fontFamily:'DM Mono,monospace',
          }}>
            Loading inventory...
          </div>
        ) : items.length === 0 ? (
          <div style={{
            padding:'60px', textAlign:'center',
            color:'var(--text3)',
          }}>
            <div style={{
              fontSize:'40px',
              marginBottom:'12px',
            }}>⊞</div>
            <div style={{
              fontSize:'14px',
              color:'var(--text2)',
              marginBottom:'6px',
            }}>
              No inventory items yet
            </div>
            <div style={{ fontSize:'12px' }}>
              Click "+ Add Item" to add
              your first product
            </div>
          </div>
        ) : (
          <table style={{
            width:'100%',
            borderCollapse:'collapse',
          }}>
            <thead>
              <tr>
                {[
                  'Product','SKU','Grade',
                  'Type','Available','Assigned',
                  'Sale Price','Rental/Mo'
                ].map(h => (
                  <th key={h} style={{
                    padding:'10px 16px',
                    textAlign:'left',
                    fontSize:'10px',
                    fontFamily:
                      'DM Mono,monospace',
                    color:'var(--text3)',
                    letterSpacing:'1.5px',
                    textTransform:'uppercase',
                    borderBottom:
                      '1px solid var(--border)',
                    background:'var(--surface2)',
                    whiteSpace:'nowrap',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id}
                  onClick={() => {
                    setSelected(item);
                    loadDetail(item.id);
                    setEditing(false);
                  }}
                  style={{
                    borderBottom:
                      '1px solid var(--border)',
                    cursor:'pointer',
                    background:
                      selected?.id === item.id
                        ? 'rgba(0,212,255,0.05)'
                        : 'transparent',
                  }}
                >
                  <td style={{
                    padding:'12px 16px',
                  }}>
                    <div style={{
                      fontWeight:'600',
                      fontSize:'13px',
                    }}>
                      {item.product_name}
                    </div>
                    {item.serial_number && (
                      <div style={{
                        fontSize:'10px',
                        color:'var(--text3)',
                        fontFamily:
                          'DM Mono,monospace',
                        marginTop:'2px',
                      }}>
                        S/N: {item.serial_number}
                      </div>
                    )}
                  </td>
                  <td style={{
                    padding:'12px 16px',
                    fontSize:'12px',
                    fontFamily:
                      'DM Mono,monospace',
                    color:'var(--accent)',
                  }}>
                    {item.sku || '—'}
                  </td>
                  <td style={{
                    padding:'12px 16px',
                  }}>
                    <span style={{
                      color: gradeColor(
                        item.grade),
                      fontFamily:
                        'DM Mono,monospace',
                      fontSize:'12px',
                      fontWeight:'600',
                    }}>
                      {item.grade}
                    </span>
                  </td>
                  <td style={{
                    padding:'12px 16px',
                  }}>
                    <span style={{
                      background:
                        item.order_type ===
                        'sale'
                          ? 'rgba(0,212,255,0.1)'
                        : item.order_type ===
                          'rental'
                          ? 'rgba(159,122,234,0.1)'
                        : 'rgba(0,229,160,0.1)',
                      color:
                        item.order_type === 'sale'
                          ? 'var(--accent)'
                        : item.order_type ===
                          'rental'
                          ? 'var(--purple)'
                        : 'var(--green)',
                      padding:'3px 10px',
                      borderRadius:'20px',
                      fontSize:'11px',
                      fontFamily:
                        'DM Mono,monospace',
                      textTransform:'capitalize',
                    }}>
                      {item.order_type}
                    </span>
                  </td>
                  <td style={{
                    padding:'12px 16px',
                    textAlign:'center',
                    fontFamily:
                      'DM Mono,monospace',
                    color:
                      item.quantity_available > 0
                        ? 'var(--green)'
                        : 'var(--red)',
                    fontWeight:'600',
                  }}>
                    {item.quantity_available}
                  </td>
                  <td style={{
                    padding:'12px 16px',
                    textAlign:'center',
                    fontFamily:
                      'DM Mono,monospace',
                    color:'var(--yellow)',
                  }}>
                    {item.quantity_assigned}
                  </td>
                  <td style={{
                    padding:'12px 16px',
                    fontFamily:
                      'DM Mono,monospace',
                    fontSize:'13px',
                    color:'var(--text2)',
                  }}>
                    {item.sale_price
                      ? `₹${Number(
                          item.sale_price
                        ).toLocaleString('en-IN')}`
                      : '—'}
                  </td>
                  <td style={{
                    padding:'12px 16px',
                    fontFamily:
                      'DM Mono,monospace',
                    fontSize:'13px',
                    color:'var(--text2)',
                  }}>
                    {item.rental_price_monthly
                      ? `₹${Number(
                          item.rental_price_monthly
                        ).toLocaleString('en-IN')}`
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail Panel */}
      {selected && detail && (
        <div style={{
          background:'var(--surface)',
          border:'1px solid var(--border)',
          borderRadius:'12px',
          overflow:'hidden',
          height:'fit-content',
        }}>
          <div style={{
            padding:'16px 20px',
            borderBottom:
              '1px solid var(--border)',
            display:'flex',
            justifyContent:'space-between',
            alignItems:'center',
          }}>
            <div style={{
              fontFamily:'Syne,sans-serif',
              fontSize:'14px',
              fontWeight:'700',
            }}>
              Item Details
            </div>
            <div style={{
              display:'flex',
              alignItems:'center',
              gap:'12px',
            }}>
              {!editing && (
                <button
                  onClick={openEdit}
                  style={{
                    background:'none',
                    border:'none',
                    color:'var(--accent)',
                    cursor:'pointer',
                    fontSize:'12px',
                    fontWeight:'600',
                  }}
                >
                  Edit
                </button>
              )}
              <button
                onClick={() => {
                  setSelected(null);
                  setDetail(null);
                  setEditing(false);
                }}
                style={{
                  background:'none',
                  border:'none',
                  color:'var(--text3)',
                  cursor:'pointer',
                  fontSize:'18px',
                }}
              >
                ✕
              </button>
            </div>
          </div>

          <div style={{ padding:'20px' }}>
            {editing ? (
              <>
                <div>
                  <label style={smallLabelStyle}>
                    Product Name
                  </label>
                  <input
                    style={smallInputStyle}
                    placeholder={
                      detail.item.catalog_model
                        ? `${detail.item.catalog_brand} ${detail.item.catalog_model} (catalog item)`
                        : ''
                    }
                    value={editForm.custom_product_name}
                    onChange={e => setEditForm({
                      ...editForm,
                      custom_product_name:e.target.value
                    })}
                  />
                </div>
                <div>
                  <label style={smallLabelStyle}>
                    SKU
                  </label>
                  <input
                    style={smallInputStyle}
                    value={editForm.sku}
                    onChange={e => setEditForm({
                      ...editForm,
                      sku:e.target.value
                    })}
                  />
                </div>
                <div>
                  <label style={smallLabelStyle}>
                    Serial Number
                  </label>
                  <input
                    style={smallInputStyle}
                    value={editForm.serial_number}
                    onChange={e => setEditForm({
                      ...editForm,
                      serial_number:e.target.value
                    })}
                  />
                </div>
                <div>
                  <label style={smallLabelStyle}>
                    Grade
                  </label>
                  <select
                    style={smallInputStyle}
                    value={editForm.grade}
                    onChange={e => setEditForm({
                      ...editForm,
                      grade:e.target.value
                    })}
                  >
                    <option value="New">New</option>
                    <option value="A+">Grade A+</option>
                    <option value="A">Grade A</option>
                    <option value="B+">Grade B+</option>
                    <option value="B">Grade B</option>
                    <option value="C">Grade C</option>
                  </select>
                </div>
                <div>
                  <label style={smallLabelStyle}>
                    Order Type
                  </label>
                  <select
                    style={smallInputStyle}
                    value={editForm.order_type}
                    onChange={e => setEditForm({
                      ...editForm,
                      order_type:e.target.value
                    })}
                  >
                    <option value="sale">Sale</option>
                    <option value="rental">Rental</option>
                    <option value="both">Both</option>
                  </select>
                </div>
                <div>
                  <label style={smallLabelStyle}>
                    Sale Price (₹)
                  </label>
                  <input
                    type="number"
                    style={smallInputStyle}
                    value={editForm.sale_price}
                    onChange={e => setEditForm({
                      ...editForm,
                      sale_price:e.target.value
                    })}
                  />
                </div>
                <div>
                  <label style={smallLabelStyle}>
                    Rental / Month (₹)
                  </label>
                  <input
                    type="number"
                    style={smallInputStyle}
                    value={editForm.rental_price_monthly}
                    onChange={e => setEditForm({
                      ...editForm,
                      rental_price_monthly:e.target.value
                    })}
                  />
                </div>
                <div>
                  <label style={smallLabelStyle}>
                    Security Deposit (₹)
                  </label>
                  <input
                    type="number"
                    style={smallInputStyle}
                    value={editForm.security_deposit}
                    onChange={e => setEditForm({
                      ...editForm,
                      security_deposit:e.target.value
                    })}
                  />
                </div>
                <div>
                  <label style={smallLabelStyle}>
                    Total Quantity
                  </label>
                  <input
                    type="number" min="0"
                    style={smallInputStyle}
                    value={editForm.quantity_total}
                    onChange={e => setEditForm({
                      ...editForm,
                      quantity_total:e.target.value
                    })}
                  />
                </div>
                <div>
                  <label style={smallLabelStyle}>
                    Condition Notes
                  </label>
                  <textarea
                    style={{
                      ...smallInputStyle,
                      minHeight:'50px',
                      resize:'vertical',
                    }}
                    value={editForm.condition_notes}
                    onChange={e => setEditForm({
                      ...editForm,
                      condition_notes:e.target.value
                    })}
                  />
                </div>

                {editError && (
                  <div style={{
                    background:'rgba(255,77,109,0.1)',
                    border:'1px solid rgba(255,77,109,0.3)',
                    color:'var(--red)',
                    padding:'8px 10px',
                    borderRadius:'6px',
                    fontSize:'12px',
                    marginBottom:'10px',
                  }}>
                    {editError}
                  </div>
                )}

                <div style={{ display:'flex', gap:'8px' }}>
                  <button
                    onClick={handleSaveEdit}
                    disabled={savingEdit}
                    style={{
                      flex:1,
                      background:'var(--accent)',
                      color:'var(--bg)',
                      border:'none',
                      padding:'9px',
                      borderRadius:'6px',
                      fontSize:'12.5px',
                      fontWeight:'600',
                      cursor: savingEdit
                        ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {savingEdit ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setEditing(false);
                      setEditError('');
                    }}
                    style={{
                      flex:1,
                      background:'transparent',
                      color:'var(--text2)',
                      border:'1px solid var(--border2)',
                      padding:'9px',
                      borderRadius:'6px',
                      fontSize:'12.5px',
                      cursor:'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{
                  fontFamily:'Syne,sans-serif',
                  fontSize:'16px',
                  fontWeight:'700',
                  marginBottom:'16px',
                }}>
                  {detail.item.product_name}
                </div>

                {[
                  ['SKU', detail.item.sku || '—'],
                  ['Serial Number',
                    detail.item.serial_number || '—'],
                  ['Grade', detail.item.grade],
                  ['Order Type', detail.item.order_type],
                  ['Sale Price',
                    detail.item.sale_price
                      ? `₹${Number(detail.item.sale_price).toLocaleString('en-IN')}`
                      : '—'],
                  ['Rental / Month',
                    detail.item.rental_price_monthly
                      ? `₹${Number(detail.item.rental_price_monthly).toLocaleString('en-IN')}`
                      : '—'],
                  ['Security Deposit',
                    detail.item.security_deposit
                      ? `₹${Number(detail.item.security_deposit).toLocaleString('en-IN')}`
                      : '—'],
                  ['Total Quantity', detail.item.quantity_total],
                  ['Available', detail.item.quantity_available],
                  ['Assigned', detail.item.quantity_assigned],
                ].map(([label, value]) => (
                  <div key={label} style={{
                    display:'flex',
                    justifyContent:'space-between',
                    padding:'8px 0',
                    borderBottom:'1px solid var(--border)',
                    fontSize:'12.5px',
                  }}>
                    <span style={{
                      color:'var(--text3)',
                      fontFamily:'DM Mono,monospace',
                      fontSize:'11px',
                    }}>
                      {label}
                    </span>
                    <span style={{
                      color:'var(--text)',
                      fontWeight:'500',
                    }}>
                      {value}
                    </span>
                  </div>
                ))}

                {detail.item.condition_notes && (
                  <div style={{
                    marginTop:'12px',
                    padding:'12px',
                    background:'var(--surface2)',
                    borderRadius:'8px',
                    fontSize:'12.5px',
                    color:'var(--text2)',
                  }}>
                    <div style={{
                      fontSize:'10px',
                      fontFamily:'DM Mono,monospace',
                      color:'var(--text3)',
                      marginBottom:'6px',
                      letterSpacing:'1px',
                    }}>
                      CONDITION NOTES
                    </div>
                    {detail.item.condition_notes}
                  </div>
                )}

                {detail.assignment_history?.length > 0 && (
                  <div style={{ marginTop:'16px' }}>
                    <div style={{
                      fontSize:'11px',
                      fontFamily:'DM Mono,monospace',
                      color:'var(--text3)',
                      letterSpacing:'1px',
                      marginBottom:'8px',
                    }}>
                      ASSIGNMENT HISTORY
                    </div>
                    {detail.assignment_history
                      .slice(0,5).map((h, idx) => (
                      <div key={idx} style={{
                        display:'flex',
                        justifyContent:'space-between',
                        alignItems:'center',
                        padding:'8px 0',
                        borderBottom:'1px solid var(--border)',
                        fontSize:'12px',
                      }}>
                        <div>
                          <span style={{
                            color:'var(--accent)',
                            fontFamily:'DM Mono,monospace',
                          }}>
                            {h.ticket_number}
                          </span>
                          <span style={{
                            color:'var(--text3)',
                            marginLeft:'8px',
                          }}>
                            {h.customer_name || '—'}
                          </span>
                        </div>
                        <span style={{
                          fontSize:'10px',
                          padding:'2px 8px',
                          borderRadius:'10px',
                          background:'rgba(255,192,64,0.1)',
                          color:'var(--yellow)',
                        }}>
                          {h.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  onClick={() =>
                    handleDeleteItem(selected.id)}
                  disabled={deleting}
                  style={{
                    width:'100%',
                    background:'rgba(255,77,109,0.1)',
                    color:'var(--red)',
                    border:'1px solid rgba(255,77,109,0.3)',
                    padding:'10px',
                    borderRadius:'8px',
                    fontSize:'13px',
                    fontWeight:'600',
                    cursor: deleting
                      ? 'not-allowed' : 'pointer',
                    marginTop:'16px',
                  }}
                >
                  {deleting ? 'Deleting...' : 'Delete Item'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Inventory;